import { ComponentProps, useEffect, useMemo, useRef, useState } from "react";
import currency from "currency.js";
import { helpers, Enums, Constants } from "@shared";
import UpsertPayor from "./upsert-payor";
import PayorAllocationCard from "./payor-allocation-card";
import { ChevronLeftIcon } from "@icons";
import { Button, IconButton } from "@common";
import { Plus } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { sunboundPayProxy } from '@proxies';
import { useSpinnerContext } from '@contexts';
import { useFormSubmission } from "@hooks";
import { StatusCodes } from "http-status-codes";
import type { Invoice, SessionData, AccountPayor, InvoicePayor, Limits } from '@app-types';
import { isApiError } from '@app-types';

type EnrollmentPayor = Omit<InvoicePayor, `idInvitation` | `isGuarantor` | `paid`> & {
    idInvitation?: string; // Account payors won't have this
    isGuarantor?: boolean; // Historically omitted from payload
    paid?: number;         // Account payors won't have this
};

type AmountKey = `amountType` | `payAmount` | `payPercentage`;

type SelectionPayor =
    Omit<AccountPayor, `payor` | AmountKey> &
    Pick<AccountPayor[`payor`], `email` | `fullName`> &
    // An account/selection/available payor originates (from the API) with the
    // "other" amount set to null, but that amount might be set to a number (in
    // the UI) as a result of editing the payor and him becoming the current
    // payor.  If afterward the user cancels the edit, then the current payor
    // (with a numeric "other" amount) becomes a selection payor again.  Hence
    // the redefined types with instances of "number | null" below:
    ({
        amountType: null;
        payAmount: null;
        payPercentage: null;
    } | {
        amountType: Enums.AmountType.Amount;
        payAmount: number;
        payPercentage: number | null;
    } | {
        amountType: Enums.AmountType.Percentage;
        payAmount: number | null;
        payPercentage: number;
    });

type CurrentSelectionPayor =
    Omit<SelectionPayor, AmountKey> &
    { alreadySelected: false } &
    ({
        amountType: null;
        payAmount: null;
        payPercentage: null;
    } | {
        // When we make an account/selection/available payor the current payor
        // (to edit him), we first ensure that we've calculated equivalent
        // amount/percentage values, regardless of the amountType.  Thus they
        // are both guaranteed to be numbers.
        amountType: Enums.AmountType.Amount | Enums.AmountType.Percentage;
        payAmount: number;
        payPercentage: number;
    });

type CurrentPayor = EnrollmentPayor | CurrentSelectionPayor;

interface InvoicePayorsProps {
    accountPayors: AccountPayor[];
    invoice: Invoice;
    invoicePayors: InvoicePayor[];
    idProductType: number;
    account: SessionData;
    idEditPayor?: number;
}

const InvoicePayors = ({ accountPayors, invoice, invoicePayors, idProductType, account, idEditPayor }: InvoicePayorsProps) => {

    const navigate = useNavigate();
    const location = useLocation();
    const { setSpinnerShow } = useSpinnerContext();
    const { setFormState, clearFormState, hasFormError, formError } = useFormSubmission();
    const { dueDate: invoiceDueDate, sunboundTotal } = invoice || {};
    const {
        idPayor: idCurrentPayor,
        orgDays: { dueDay: orgDueDay },
        potDeadline,
        idAccount,
        accountPreferences: { dueDay: preferenceDueDay, isAutoPay }
    } = account;

    const [payors, setPayors] = useState<EnrollmentPayor[]>([]);
    const [initialPayors, setInitialPayors] = useState<EnrollmentPayor[]>([]);
    const [currentPayor, setCurrentPayor] = useState<CurrentPayor | null>(null);
    const [selectionPayors, setSelectionPayors] = useState<SelectionPayor[]>([]);
    const [editing, setEditing] = useState(false);
    const hasAutoEditedRef = useRef(false);

    useEffect(() => {
        const { adjustedPayors, availPayors } = processPayorsLists(accountPayors, invoicePayors);
        setPayors(adjustedPayors);
        setInitialPayors(adjustedPayors.map(p => ({ ...p })));
        setSelectionPayors(availPayors);
    }, []);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const sortPayors = <T extends { idPayor: number }>(payors: T[]): T[] => (
        [...payors].sort((a, b) => {
            // Current payor always first
            if (a.idPayor === idCurrentPayor) return -1;
            if (b.idPayor === idCurrentPayor) return 1;
            // Otherwise sort by idPayor ascending
            return a.idPayor - b.idPayor;
        })
    );

    const processPayorsLists = (accountPayors: AccountPayor[], invoicePayors: InvoicePayor[]) => {
        const availPayorsFilter = accountPayors.filter(ap => {
            const { isGuarantor, isActive } = ap;

            return !isGuarantor && isActive;
        });
        const availPayors = availPayorsFilter.map(avail => {
            const { payor, ...rest } = avail;
            const { fullName, email } = payor;
            return { ...rest, fullName, email };
        });
        const adjustedPayors = invoicePayors.map(p => ({ ...p }));

        adjustedPayors.forEach((p) => {
            const index = availPayors.findIndex((sp) => sp.idPayor === p.idPayor);
            if (index !== -1) {
                availPayors.splice(index, 1);
            }
        });

        return {
            adjustedPayors: sortPayors(adjustedPayors),
            availPayors: sortPayors(availPayors)
        };
    };

    const upsert: ComponentProps<typeof UpsertPayor>[`upsert`] = (payor) => {

        const { idPayor, idInvitation, email, fullName, payAmount, payPercentage, dueDay, amountType, alreadySelected, paid } = payor;

        // Check if amount/percentage is 0 - this means remove allocation
        const isZero = currency(payAmount).value === 0 || currency(payPercentage, { precision: 4 }).value === 0;

        if (isZero) {
            if (alreadySelected) {
                // This payor was already allocated and is being set to 0 - move to selectionPayors
                const accountPayor = accountPayors.find(p => p.idPayor === idPayor);
                if (accountPayor) {
                    const { payor, ...rest } = accountPayor;
                    const { fullName, email } = payor;
                    const newSelectionPayors = [...selectionPayors];
                    newSelectionPayors.push({ ...rest, fullName, email });
                    setSelectionPayors(sortPayors(newSelectionPayors));
                }
                // Recalculate guarantor amount with current payors (without this one)
                calculate([...payors]);
            } else {
                // Setting allocation for first time as 0 - just cancel (no-op)
                const newSelectionPayors = [...selectionPayors];
                newSelectionPayors.push({ ...payor });
                setSelectionPayors(sortPayors(newSelectionPayors));
            }
            setCurrentPayor(null);
            setEditing(false);
            return;
        }

        const adjustedDueDay = idProductType === Enums.ProductType.SunboundPay ? potDeadline : (dueDay ?? orgDueDay);

        const newPayor: EnrollmentPayor = {
            idPayor,
            idInvitation,
            email,
            fullName,
            payAmount: currency(payAmount).value,
            payPercentage: currency(payPercentage, { precision: 4 }).value,
            dueDay: adjustedDueDay,
            amountType,
            alreadySelected: true, // For TS compiler; normalize to satisfy invariant
            paid
        };

        const newPayors = [...payors];
        newPayors.push(newPayor);

        calculate(newPayors);
        setPayors(sortPayors(newPayors));
        setCurrentPayor(null);
        setEditing(false);
    };

    const calculate = (newPayors: EnrollmentPayor[]) => {
        let rest = currency(sunboundTotal);

        newPayors.forEach(ap => {
            const { payAmount, isGuarantor = false } = ap;
            if (!isGuarantor)
                rest = rest.subtract(payAmount);
        });
        const guarantor = findGuarantor(newPayors);
        if (!guarantor) return; // For TS compiler; every account has 1 guarantor

        guarantor.payAmount = rest.value;
        guarantor.payPercentage = helpers.percentage(guarantor.payAmount, sunboundTotal);
    };

    const cancelEditing = () => {

        if (currentPayor !== null) {
            if (currentPayor.alreadySelected) {
                const newPayors = [...payors];
                newPayors.push({ ...currentPayor });
                calculate(newPayors);
                setPayors(sortPayors(newPayors));

            } else if (currentPayor.idPayor) {
                const newSelectionPayors = [...selectionPayors];
                newSelectionPayors.push({ ...currentPayor });
                setSelectionPayors(sortPayors(newSelectionPayors));
            }
        }

        setCurrentPayor(null);
        setEditing(false);
    };

    const editPayor = (idPayor: number) => {
        const newPayors = [...payors];
        const payorIndex = newPayors.findIndex(p => p.idPayor === idPayor);
        if (payorIndex === -1) return;

        const removed = newPayors.splice(payorIndex, 1);
        const removedPayor = removed[0];
        if (!removedPayor) return; // For TS compiler; guaranteed defined by logic

        calculate(newPayors);
        setCurrentPayor({ ...removedPayor, alreadySelected: true });
        setPayors(newPayors);
        setEditing(true);
    };

    const selectPayor = (index: number) => {
        const newSelectionPayors = [...selectionPayors];
        const [add] = newSelectionPayors.splice(index, 1);
        if (!add) return; // For TS compiler; guaranteed defined by logic

        setSelectionPayors(newSelectionPayors);
        if (add.amountType === Enums.AmountType.Percentage) {
            const payAmount = helpers.toFixedDown(currency(add.payPercentage).multiply(sunboundTotal).value, 2);
            setCurrentPayor({ ...add, alreadySelected: false, payAmount });
        } else if (add.amountType === Enums.AmountType.Amount) {
            const payPercentage = helpers.toFixedDown(helpers.percentage(add.payAmount, sunboundTotal), 2);
            setCurrentPayor({ ...add, alreadySelected: false, payPercentage });
        } else {
            setCurrentPayor({ ...add, alreadySelected: false });
        }
        setEditing(true);
    };

    const payorsConfiguration =
        payors.map((p, index) => {
            const {
                payPercentage,
                payAmount,
                fullName,
                idPayor,
                dueDay,
                isGuarantor
            } = p;

            const nameLabel = idPayor === idCurrentPayor ? `You` : fullName;
            const autopayDate = invoiceDueDate && dueDay !== null ? helpers.compoundDay(invoiceDueDate, dueDay).toFormat(`MMM dd`) : ``;
            const percentage = `${helpers.printPercentage(payPercentage)}%`;
            const amount = currency(payAmount).format();

            return (
                <PayorAllocationCard
                    key={`${idPayor}-${index}`}
                    name={nameLabel}
                    column1={<span className="text-neutral-950">{autopayDate}</span>}
                    column2={<span className="text-neutral-950">{percentage}</span>}
                    column3={<span className="text-neutral-950">{amount}</span>}
                    showEditButton={!isGuarantor}
                    editButtonTooltip={
                        idPayor === idCurrentPayor ? `Edit your allocation` :
                        `Edit allocation`
                    }
                    onEdit={() => editPayor(idPayor)}
                    editButtonDisabled={editing}
                />
            );
        });

    const saveChanges = async () => {
        setSpinnerShow(true);
        try {
            clearFormState();

            // Patch payors with correct due days
            const patchPayors = payors.map(p => {
                let { dueDay } = p;
                const { idPayor } = p;
                if (idPayor === idCurrentPayor) {
                    if (idProductType === Enums.ProductType.SunboundPay && !isAutoPay) {
                        dueDay = potDeadline;
                    } else {
                        dueDay = preferenceDueDay;
                    }
                }
                return { ...p, dueDay };
            });

            // Update the enrollment
            await sunboundPayProxy.updateEnrollment(idAccount, invoice.idInvoice, idProductType, patchPayors);

            // Navigate using the flow helper
            helpers.navigateNext(location, navigate);
        } catch (error) {
            console.error(error);
            if (isApiError(error) && error.response?.status !== undefined && error.response.status >= StatusCodes.BAD_REQUEST && error.response.status < StatusCodes.INTERNAL_SERVER_ERROR) {
                setFormState(error.response?.data?.message || Constants.Errors.INTERNAL_ERROR);
            } else {
                setFormState(Constants.Errors.INTERNAL_ERROR);
            }
        }
        setSpinnerShow(false);
    };

    const cancel = () => {
        navigate(-1);
    };

    const payorSelector = (selectionPayors: SelectionPayor[], editing: boolean) => (
        <>
            {
                selectionPayors.map((p, index) => {
                    const { email, fullName } = p;

                    return (
                        <PayorAllocationCard
                            key={email}
                            name={fullName}
                            column1={
                                <span className="italic text-neutral-950">
                                    Not set
                                </span>
                            }
                            column2={<span className="text-neutral-500">- -</span>}
                            column3={<span className="text-neutral-500">- -</span>}
                            showEditButton={true}
                            editButtonTooltip="Set allocation"
                            onEdit={() => selectPayor(index)}
                            editButtonDisabled={editing}
                        />
                    );
                })
            }
        </>
    );

    const findGuarantor = (payors: EnrollmentPayor[]) => {
        // The guarantor is immutable.  It's helpful to know that it always has
        // the "optional" props defined, for the sake of strict type checking in
        // this module; hence the type predicate below:
        const guarantor = payors.find((p): p is InvoicePayor => !!p.isGuarantor);
        return guarantor;
    };

    const getLimits = (payors: EnrollmentPayor[]): Limits | null => {
        if (payors.length === 0) {
            return null;
        }
        const guarantor = findGuarantor(payors);
        if (!guarantor) return null; // For TS compiler; every account has 1 guarantor

        return {
            max: currency(guarantor.payAmount).subtract(guarantor.paid).value,
            min: guarantor.paid || Constants.System.MIN_PAYMENT_AMOUNT
        };
    };

    const limits = useMemo(() => getLimits(payors), [payors]);

    const allocatableAmount = useMemo(() => {
        const guarantor = findGuarantor(payors);
        if (!guarantor) return null; // For TS compiler; every account has 1 guarantor

        const { payAmount, paid } = guarantor;
        return currency(payAmount).subtract(paid).format();
    }, [payors]);

    const hasChanges = useMemo(() => {
        // If initial state hasn't been set yet, no changes
        if (initialPayors.length === 0) {
            return false;
        }

        // Check if number of payors changed
        if (payors.length !== initialPayors.length) {
            return true;
        }

        // Check if any payor's allocation has changed
        return payors.some(currentPayor => {
            const initialPayor = initialPayors.find(p => p.idPayor === currentPayor.idPayor);

            // New payor added
            if (!initialPayor) {
                return true;
            }

            // Compare amounts (use currency for proper comparison)
            const amountChanged = currency(currentPayor.payAmount).value !== currency(initialPayor.payAmount).value;

            // Compare percentages
            const percentageChanged = currency(currentPayor.payPercentage, { precision: 4 }).value !==
                                     currency(initialPayor.payPercentage, { precision: 4 }).value;

            return amountChanged || percentageChanged;
        });
    }, [payors, initialPayors]);

    const handleAddPayor = () => {
        navigate(`/add-payor`);
    };

    // Auto-open edit for specified payor
    useEffect(() => {
        if (!hasAutoEditedRef.current && idEditPayor && payors.length > 0) {
            editPayor(idEditPayor);
            hasAutoEditedRef.current = true;
        }
    }, [idEditPayor, payors.length, editPayor]);

    return (
        <div className="flex flex-col w-full">
            {/* Header */}
            <div className="border-b border-neutral-100 pb-6 mb-8 flex items-center justify-between gap-4">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                    <IconButton
                        variant="tertiary"
                        size="sm"
                        className="flex-shrink-0 md:btn-icon-lg"
                        onClick={cancel}
                        aria-label="Go back"
                    >
                        <ChevronLeftIcon />
                    </IconButton>
                    <h1 className="text-[20px] leading-[24px] md:text-[28px] md:leading-[40px] font-medium text-neutral-950 tracking-[0.2px] md:tracking-[0.28px] truncate">
                        Payor Allocation
                    </h1>
                </div>
                <Button
                    variant="primary"
                    size="md"
                    startIcon={<Plus />}
                    className="btn-icon-lg md:btn-text-md flex-shrink-0"
                    onClick={handleAddPayor}
                    aria-label="Add Payor"
                >
                    <span className="hidden md:inline">Payor</span>
                </Button>
            </div>

            {/* Content */}
            <div className="flex flex-col gap-4">
                {/* Allocatable Amount and Instructions */}
                <div className="flex flex-col gap-2">
                    <h2 className="text-[20px] leading-[24px] font-medium text-neutral-950 tracking-[0.2px]">
                        Allocatable amount: {allocatableAmount || `$0.00`}
                    </h2>
                    <p className="text-sm leading-5 text-neutral-800 tracking-[0.14px]">
                        All active Payors are listed below. Changes are applied to all bills moving forward.
                    </p>
                </div>

                {/* Payor Allocations and Unallocated Payors */}
                <div className="flex flex-col gap-4">
                    {/* Payor Allocations */}
                    {payors.length > 0 && (
                        <div className="flex flex-col gap-4">
                            <h2 className="text-base leading-6 font-medium text-neutral-950 tracking-[0.16px]">
                                Payor Allocations
                            </h2>
                            <div className="flex flex-col gap-4">
                                {payorsConfiguration}
                            </div>
                        </div>
                    )}

                    {/* Existing Payors Selector */}
                    {payorSelector(selectionPayors, editing)}
                </div>

                {/* Editing Form */}
                {editing && currentPayor && limits && (
                    <UpsertPayor
                        sunboundTotal={sunboundTotal}
                        upsert={upsert}
                        limits={limits}
                        cancelEditing={cancelEditing}
                        payor={currentPayor} />
                )}
            </div>

            {/* Action Buttons */}
            {!editing && (
                <div className="flex flex-col gap-4 w-full pt-8">
                    {hasChanges && (
                        <p className="text-sm leading-5 text-neutral-800 tracking-[0.14px] text-center">
                            Changes have not been saved. Click below to finish updates.
                        </p>
                    )}
                    <div className="flex flex-col md:flex-row gap-4 w-full">
                        <Button
                            variant="primary"
                            size="lg"
                            className="order-1 md:order-2 flex-1"
                            onClick={saveChanges}
                            disabled={!hasChanges}
                        >
                            Save Changes
                        </Button>
                        <Button
                            variant="secondary"
                            size="lg"
                            className="order-2 md:order-1 flex-1"
                            onClick={cancel}
                        >
                            Cancel
                        </Button>
                    </div>
                    {hasFormError && (
                        <p className="text-sm leading-5 text-red-600 tracking-[0.14px] w-full text-center">{formError}</p>
                    )}
                </div>
            )}
        </div>
    );

};

export default InvoicePayors;
